import React, { useState, useCallback } from 'react';
import SectionEdition, { Slot } from 'components/styleguide/section_edition/SectionEdition';
import Sheet from 'components/styleguide/sheet_modal/Sheet';
import ImageUpload from '../../styleguide/image_upload/ImageUpload';
import { getCSRFToken } from 'lib/request';

interface CompanyLogoSectionProps {
  logoUrl: string;
  url: string;
  errors?: Record<string, string>;
}

const CompanyLogoSection: React.FC<CompanyLogoSectionProps> = ({
  logoUrl: initialLogoUrl,
  url,
  errors,
}) => {
  const [logoUrl, setLogoUrl] = useState(initialLogoUrl || '');
  const [isOpen, setIsOpen] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (selectedFile: File) => {
    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
    setError(null);
  };

  const handleSave = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('company[logo]', file || '');

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': getCSRFToken() || '',
          'Accept': 'application/json',
        },
        body: formData,
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setLogoUrl(data.logo_url || preview || logoUrl);
        setIsOpen(false);
        setPreview(null);
        setFile(null);
        setError(null);
      } else {
        setError(data.errors?.logo || null);
      }
    } catch (error) {
      // Handle error (e.g., show notification)
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, file, url, preview, logoUrl]);

  const handleClose = () => {
    setIsOpen(false);
    setPreview(null);
    setFile(null);
    setError(null);
  };

  const status = logoUrl ? 'complete' : errors?.logo ? 'mandatory' : 'missing';

  return (
    <>
      <SectionEdition
        title="Company logo"
        iconName="image"
        context="company"
        status={status}
        onEdit={() => setIsOpen(true)}
      >
        <Slot>
          <ImageUpload imageUrl={logoUrl} readOnly />
        </Slot>
      </SectionEdition>

      {isOpen && (
        <Sheet
          title="Edit company logo"
          onClose={handleClose}
          context="company"
          showCancelButton={false}
          continueButtonText="Save"
          onContinue={handleSave}
        >
          <ImageUpload
            imageUrl={preview || logoUrl}
            onChange={handleFileChange}
            error={error || undefined}
          />
        </Sheet>
      )}
    </>
  );
};

export default CompanyLogoSection;
